/**
 * ConfirmDialog Component - Reusable confirmation dialog with theming support
 * 
 * Variants:
 * - info: General confirmations (blue)
 * - warning: Caution actions (purple - cool alternative to yellow)
 * - danger: Destructive actions (magenta - softer than red)
 * 
 * Features:
 * - Theme-aware colors
 * - Optional text confirmation for dangerous actions
 * - Customizable buttons
 * - Keyboard support (ESC to cancel, Enter to confirm when no text required)
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useBodyScrollLock, useEscapeKey, useEnterKey } from '../hooks/useModalEffects';
import Button from './Button';
import './ConfirmDialog.css';

const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  details,
  variant = 'info',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  requireTextConfirmation = false,
  confirmationText = '',
  loading = false,
  confirmDisabled = false,
  children
}) => {
  const { theme } = useTheme();
  const [textInput, setTextInput] = useState('');
  const dialogRef = useRef();
  const inputRef = useRef();

  // Use custom hooks for modal behavior
  useBodyScrollLock(isOpen);
  useEscapeKey(isOpen, onClose);
  useEnterKey(isOpen, onConfirm, requireTextConfirmation || loading);

  // Handle initial focus when dialog opens
  useEffect(() => {
    if (isOpen) {
      setTextInput('');

      // Focus input if text confirmation required, otherwise focus dialog
      setTimeout(() => {
        if (requireTextConfirmation && inputRef.current) {
          inputRef.current.focus();
        } else {
          dialogRef.current?.focus();
        }
      }, 100);
    }
  }, [isOpen, requireTextConfirmation]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleConfirm = () => {
    if (requireTextConfirmation && textInput !== confirmationText) {
      return;
    }
    onConfirm();
  };

  const isConfirmDisabled = loading || confirmDisabled || (requireTextConfirmation && textInput !== confirmationText);

  // Get variant button type
  const getConfirmVariant = () => {
    switch (variant) {
      case 'danger':
        return 'delete';
      case 'warning':
        return 'warning';
      case 'info':
      default:
        return 'primary';
    }
  };

  return (
    <div className="confirm-dialog-overlay" onClick={handleOverlayClick}>
      <div
        ref={dialogRef}
        className={`confirm-dialog confirm-dialog--${variant}`}
        tabIndex={-1}
        role="alertdialog"
        aria-labelledby="dialog-title"
        aria-describedby="dialog-message"
      >
        <div className="confirm-dialog__header">
          <h3 id="dialog-title" className="confirm-dialog__title">
            {title}
          </h3>
          <button
            className="confirm-dialog__close"
            onClick={onClose}
            aria-label="Close dialog"
            disabled={loading}
          >
            ✕
          </button>
        </div>

        <div className="confirm-dialog__content">
          <p id="dialog-message" className="confirm-dialog__message">
            {message}
          </p>

          {details && (
            <div className="confirm-dialog__details">
              {details}
            </div>
          )}

          {children}

          {requireTextConfirmation && (
            <div className="confirm-dialog__confirmation">
              <p className="confirm-dialog__confirmation-label">
                To confirm, type <strong>{confirmationText}</strong>
              </p>
              <input
                ref={inputRef}
                type="text"
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                placeholder={`Type "${confirmationText}" to confirm`}
                className="confirm-dialog__confirmation-input"
                disabled={loading}
              />
            </div>
          )}
        </div>

        <div className="confirm-dialog__actions">
          <Button
            onClick={onClose}
            variant="cancel"
            disabled={loading}
          >
            {cancelText}
          </Button>
          <Button
            onClick={handleConfirm}
            variant={getConfirmVariant()}
            disabled={isConfirmDisabled}
            loading={loading}
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;

